/**
 * `/datasets` — list, create, edit, delete, and refresh tracker datasets.
 *
 * A dataset is a reusable configuration pointing at a tracker source
 * with JQL/WIQL queries, workflow mapping, and adapter config. Every
 * signed-in user sees all datasets; only admins create, edit, delete or
 * sync them here (shipcast-o7j.1, enforced server-side). Users get fresh
 * data into a team from the team itself, not from this page.
 *
 * Refresh flow: POST /ingest returns a new running ingestion → the row
 * polls `latest-ingestion` every 2s until the status settles. The
 * staleness badge reads from the latest ok ingestion's
 * `completed_at`.
 */

import { useState } from 'react';
import { AlertTriangle, Loader2, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@lean-agility/design-system/react';

import { Dialog, DialogActions } from '@/components/dialog';
import { getStoredUser } from '@/features/auth/utils/authStorage';
import { useDatasets, useDeleteDataset, useLatestIngestion, useSources, useTriggerIngest } from '../hooks';
import type { DatasetResponse, ImportWarning, SourceResponse } from '../types';
import { TrackerCapabilityGate } from '../components/TrackerCapabilityGate';
import { DatasetFormDialog } from '../components/DatasetFormDialog';
import { StalenessBadge } from '../components/StalenessBadge';
import { WarningPanels } from '../components/WarningPanels';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DatasetsPage() {
  return (
    <TrackerCapabilityGate>
      <DatasetsPageContent />
    </TrackerCapabilityGate>
  );
}

function DatasetsPageContent() {
  const isAdmin = !!getStoredUser()?.is_admin;
  const { data: datasets, isLoading, error } = useDatasets();
  const { data: sources, error: sourcesError } = useSources({ enabled: isAdmin });
  const [editing, setEditing] = useState<DatasetResponse | null>(null);
  const [creating, setCreating] = useState(false);

  const sourceByIdMap = new Map<string, SourceResponse>((sources ?? []).map((s) => [s.id, s]));

  return (
    <div className="container mx-auto space-y-6 px-4 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Datasets</h1>
          <p className="mt-1 text-sm text-text-muted">
            Reusable query + workflow configurations. Attach one to a simulation team to drive its deliverables from a
            tracker.
          </p>
        </div>
        {isAdmin && (
          <Button variant="primary" onClick={() => setCreating(true)} disabled={!sources || sources.length === 0}>
            <Plus className="size-4" /> New dataset
          </Button>
        )}
      </div>

      {!isAdmin && (
        <p className="text-sm text-text-muted">
          Datasets are defined by administrators. To use one, pick it on a team in your simulation.
        </p>
      )}

      {/*
        Surface the sources-load failure explicitly. Without it the only
        symptom is a silently-disabled "New dataset" button (it keys off
        `!sources`), leaving the user with no idea why. Kept distinct from
        the datasets error below so the cause is unambiguous.
      */}
      {sourcesError && (
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-feedback-error">
              Failed to load tracker sources. You won&apos;t be able to create datasets until this resolves. Please
              refresh.
            </p>
          </CardContent>
        </Card>
      )}

      {sources && sources.length === 0 && (
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-text-muted">
              No tracker sources configured yet. An administrator needs to add one at{' '}
              <code>/admin/tracker-sources</code> before you can create a dataset.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All datasets</CardTitle>
        </CardHeader>
        <CardContent>
          {error && <p className="text-sm text-feedback-error">Failed to load datasets.</p>}
          {isLoading && <p className="text-sm text-text-muted">Loading…</p>}
          {!isLoading && datasets && datasets.length === 0 && (
            <p className="text-sm text-text-muted">No datasets yet.</p>
          )}
          {datasets && datasets.length > 0 && (
            <ul className="divide-y">
              {datasets.map((ds) => (
                <DatasetRow
                  key={ds.id}
                  dataset={ds}
                  source={sourceByIdMap.get(ds.tracker_source_id)}
                  canEdit={isAdmin}
                  onEdit={() => setEditing(ds)}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {creating && sources && <DatasetFormDialog dataset={null} sources={sources} onClose={() => setCreating(false)} />}
      {editing && sources && <DatasetFormDialog dataset={editing} sources={sources} onClose={() => setEditing(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function DatasetRow({
  dataset,
  source,
  canEdit,
  onEdit,
}: {
  dataset: DatasetResponse;
  source: SourceResponse | undefined;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const triggerIngest = useTriggerIngest();
  const deleteDataset = useDeleteDataset();
  const [activeIngestionId, setActiveIngestionId] = useState<string | null>(null);
  const [showWarnings, setShowWarnings] = useState(false);

  // Poll `latest-ingestion` while a refresh is in flight. `latest` is a
  // good-enough proxy because we invalidate on trigger success and the
  // first poll reads the new `running` row.
  const poll = activeIngestionId ? 2000 : false;
  const { data: syncState } = useLatestIngestion(dataset.id, poll);
  // Two independent facts: what the last ATTEMPT did, and when the data was
  // last actually refreshed. See `LatestIngestionResponse` (shipcast-78m.4).
  const latest = syncState?.latest ?? null;

  // Stop polling once the tracked ingestion resolves. This is React's
  // "adjust state during render" escape hatch (not an effect): when the polled
  // result shows the tracked ingestion has finished, clear the tracked id so
  // the next render computes `poll = false`. Guarded by an equality check so it
  // runs at most once per resolution and never loops.
  if (activeIngestionId && latest && latest.id === activeIngestionId && latest.status !== 'running') {
    setActiveIngestionId(null);
  }

  const onRefresh = async () => {
    const res = await triggerIngest.mutateAsync(dataset.id);
    setActiveIngestionId(res.id);
  };

  const onDelete = async () => {
    if (!confirm(`Delete dataset "${dataset.name}"? Simulations using it will keep their current deliverables.`))
      return;
    await deleteDataset.mutateAsync(dataset.id);
  };

  const isRefreshing = activeIngestionId !== null || triggerIngest.isPending;
  const warnings = latest?.result?.batch.warnings ?? [];

  return (
    <li className="flex items-start gap-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{dataset.name}</span>
          <StalenessBadge syncState={syncState ?? null} />
          {latest?.status === 'running' && (
            <span className="inline-flex items-center gap-1 text-xs text-text-muted">
              <Loader2 className="size-3 animate-spin" /> syncing…
            </span>
          )}
          {latest?.status === 'error' && (
            <span
              title={latest.error_message ?? 'Unknown error'}
              className="
                inline-flex items-center rounded-sm bg-feedback-error/10 px-2 py-0.5 text-xs text-feedback-error
              "
            >
              last sync failed
            </span>
          )}
          {warnings.length > 0 && (
            <button
              type="button"
              onClick={() => setShowWarnings(true)}
              className="
                inline-flex items-center gap-1 rounded-sm bg-feedback-warning/10 px-2 py-0.5 text-xs
                text-feedback-warning
                hover:bg-feedback-warning/20
              "
              title="Show ingestion warnings"
            >
              <AlertTriangle className="size-3" />
              {warnings.length} warning{warnings.length === 1 ? '' : 's'}
            </button>
          )}
        </div>
        <p className="mt-0.5 truncate text-xs text-text-muted">
          {/* Sources are admin-only; a non-admin sees the query without its source. */}
          {source && `${source.kind.toUpperCase()} · ${source.name} · `}
          <code className="text-xs">{dataset.deliverables_query}</code>
        </p>
      </div>
      <div className="flex items-center gap-1">
        {canEdit && (
          <Button variant="ghost" size="sm" onClick={onRefresh} disabled={isRefreshing} aria-label="Refresh">
            <RefreshCw
              className={`
                size-4
                ${isRefreshing ? 'animate-spin' : ''}
              `}
            />
          </Button>
        )}
        {canEdit && (
          <Button variant="ghost" size="sm" onClick={onEdit} aria-label="Edit">
            <Pencil className="size-4" />
          </Button>
        )}
        {canEdit && (
          <Button variant="ghost" size="sm" onClick={onDelete} disabled={deleteDataset.isPending} aria-label="Delete">
            <Trash2 className="size-4" />
          </Button>
        )}
      </div>
      {showWarnings && (
        <WarningsDialog datasetName={dataset.name} warnings={warnings} onClose={() => setShowWarnings(false)} />
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Warnings dialog
// ---------------------------------------------------------------------------

/**
 * The most recent ingest's warnings for one dataset row. The panels are
 * `WarningPanels`, shared with the form's pre-save preview so a warning reads
 * identically whether it was seen before or after the dataset existed.
 */
function WarningsDialog({
  datasetName,
  warnings,
  onClose,
}: {
  datasetName: string;
  warnings: ImportWarning[];
  onClose: () => void;
}) {
  return (
    <Dialog title={`Ingestion warnings — ${datasetName}`} onClose={onClose} maxWidth="max-w-2xl">
      <div className="space-y-4 text-sm">
        <p className="text-text-muted">
          Non-fatal issues from the most recent ingest. Fix the configuration and re-sync; the dataset will keep its
          current data until then.
        </p>
        <WarningPanels warnings={warnings} />
      </div>
      <DialogActions>
        <Button variant="primary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
